/**
 * Distributed Tracing Middleware
 *
 * This middleware implements distributed tracing for GraphQL queries and API calls.
 * It creates spans with trace IDs, injects tracing headers, and logs trace data
 * for correlation with backend services.
 *
 * Based on the implementation from consumer/react-native repository.
 */

import type { Trace } from '@cbhq/client-analytics';
import { createNewSpan, getTracingHeaders, logTrace } from '@cbhq/client-analytics';
import type { NonThrowingFetch } from '@cbhq/data-layer/network';

import { getSamplingRate, TRACING_ENABLED } from './config';

/**
 * Span type from @cbhq/client-analytics
 */
type Span = {
  trace_id: string;
  span_id: string;
  name: string;
  resource: string;
  service?: string;
  type?: string;
  meta?: Record<string, string>;
};

/**
 * Request init type that may include GraphQL metadata
 * This matches RelayFetchInit from @cbhq/data-layer
 */
type RequestInitWithMetadata = RequestInit & {
  graphqlMetadata?: {
    request?: {
      name?: string;
      id?: string;
      text?: string;
    };
    variables?: Record<string, unknown>;
  };
};

/**
 * Determines if a trace should be sent based on sampling probability
 *
 * @param probability - Number between 0 and 1 representing sampling rate
 * @returns true if the trace should be sent, false otherwise
 */
function canBeSent(probability?: number): boolean {
  const samplingRate = probability ?? getSamplingRate();
  return Math.random() <= samplingRate;
}

/**
 * Extracts span name and resource name from the request
 *
 * For GraphQL requests: uses query name as resource
 * For API requests: uses URL pathname as resource
 *
 * @param url - Request URL
 * @param init - Request initialization with optional GraphQL metadata
 * @returns Object containing span name and resource name
 */
function getSpanProps(
  url: string,
  init?: RequestInitWithMetadata,
): { name: string; resource: string } {
  let defaultResourceName = url;
  let isGraphqlRequest = false;

  // Check if this is a GraphQL request by looking for graphqlMetadata
  if (init && 'graphqlMetadata' in init) {
    const { graphqlMetadata } = init;
    isGraphqlRequest = graphqlMetadata !== undefined;
    defaultResourceName = graphqlMetadata?.request?.name ?? url;
  }

  return isGraphqlRequest
    ? {
        name: 'graphql-query',
        resource: defaultResourceName,
      }
    : {
        name: 'api-request',
        resource: new URL(defaultResourceName, window.location.origin).pathname,
      };
}

/**
 * Tracing middleware for GraphQL queries and API calls
 *
 * This middleware:
 * 1. Samples requests based on configured sampling rate
 * 2. Creates a span with trace_id and span_id for each request
 * 3. Injects tracing headers to correlate with backend traces
 * 4. Logs trace data with timing information
 * 5. Handles both successful and error responses
 *
 * @param next - The next fetch function in the middleware chain
 * @returns Wrapped fetch function with tracing capabilities
 *
 * @example
 * ```ts
 * const tracedFetch = tracing(createFetch(config));
 * ```
 */
export function tracing(next: NonThrowingFetch): NonThrowingFetch {
  return async function tracingMiddleware(url: string, init?: RequestInitWithMetadata) {
    // Skip tracing if disabled or not sampled
    if (!TRACING_ENABLED || !canBeSent()) {
      return next(url, init);
    }

    /**
     * Build a tracing span object with metadata
     */
    const spanProps = getSpanProps(url, init);
    const span: Span = createNewSpan(spanProps);

    /**
     * Execute the request with injected tracing headers
     */

    const result = await next(url, {
      ...init,
      headers: (() => {
        const headers = new Headers(init?.headers);

        // Inject tracing headers to connect client traces with backend spans.
        Object.entries(getTracingHeaders(span.trace_id, span.span_id)).forEach(([key, value]) => {
          headers.set(key, value);
        });

        return headers;
      })(),
    });

    /**
     * Create a trace from the span
     * A trace is a collection of spans with the same trace_id
     */
    const trace = [span] as Trace;

    // Log traces for both successful and error responses
    if (!(result instanceof Error)) {
      const { requestTime: start, timeUntilFirstByte: duration } = result;
      /**
       * Log trace with timing attributes
       * This allows us to track request performance in Datadog
       */
      logTrace(trace, { start, duration });
    } else {
      /**
       * Log error traces with error metadata
       * Note: We don't have start/duration for errors from the fetch layer,
       * but we still want to track that the request failed
       */
      logTrace(trace, {
        error: 1,
        meta: {
          message: result.message,
        },
      });
    }

    return result;
  };
}
